// Cookie-session auth (Microsoft Entra). The browser holds an HttpOnly session
// cookie — there are no tokens for the SPA to manage. On mount we hit /auth/me
// to learn who the user is; sign-in is a redirect to /auth/microsoft.

import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setUser(data.user || null);
        }
      } catch {
        // Not signed in — that's fine; routing will send them to /login.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = () => {
    // Kick off the OIDC flow. The backend redirect lives at /auth/microsoft.
    window.location.href = '/auth/microsoft';
  };

  const logout = () => {
    // Server-side logout also signs out of Microsoft.
    window.location.href = '/auth/logout';
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    // Stubs kept so any lingering callers don't crash. They route to Entra.
    changePassword: () => ({ success: false, error: 'Passwords are managed by Microsoft.' }),
    forgotPassword: () => ({ success: false, error: 'Passwords are managed by Microsoft.' }),
    resetPassword:  () => ({ success: false, error: 'Passwords are managed by Microsoft.' }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
